import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { rawGetPuzzlesById } from 'services';
import { strings } from 'strings';

export type useAddPuzzleParams = {
  onAddItem?: (puzzle: { puzzleId: string; name: string; icon: string }) => void;
  puzzleIdsInEvent?: string[];
};

const useAddPuzzle = ({ onAddItem, puzzleIdsInEvent }: useAddPuzzleParams) => {
  const [puzzleData, setPuzzleData] = useState<any>();
  const [error, setError] = useState<string>();
  const [isLoading, setLoading] = useState<boolean>(false);

  const { handleSubmit, control, setValue, watch } = useForm<{ puzzleId: string }>();

  const { puzzleId } = watch();

  const submit = useCallback(
    handleSubmit(async (data) => {
      onAddItem &&
        onAddItem({ puzzleId: data.puzzleId, name: puzzleData.name, icon: puzzleData.iconURL });

      setValue('puzzleId', '');
    }),
    [puzzleData],
  );

  const reset = useCallback(() => {
    setValue('puzzleId', '');
  }, []);

  useEffect(() => {
    (async () => {
      setPuzzleData(null);
      setError('');

      if (puzzleId) {
        setLoading(true);

        const docSnap = await rawGetPuzzlesById(puzzleId);

        if (!docSnap.exists()) {
          setError(strings.gameEvent.form.addPuzzle.errors.puzzleNotExists);
          setLoading(false);

          return;
        }

        const puzzleData = docSnap.data();

        if (puzzleData?.status !== 'approved') {
          setError(strings.gameEvent.form.addPuzzle.errors.puzzleNotApproved);
          setLoading(false);

          return;
        }

        if (puzzleData?.premiumTier) {
          setError(strings.gameEvent.form.addPuzzle.errors.puzzleNotFree);
          setLoading(false);

          return;
        }

        if (puzzleIdsInEvent?.includes(docSnap.id)) {
          setError(strings.gameEvent.form.addPuzzle.errors.puzzleAlreadyAdded);
          setLoading(false);

          return;
        }

        setPuzzleData({ ...puzzleData, uuid: docSnap.id });
      }
      setLoading(false);
    })();
  }, [puzzleId]);

  return {
    isLoading,
    error,
    reset,
    submit,
    control,
    puzzleId,
    puzzleData,
  };
};

export default useAddPuzzle;
